import {
  type DatabaseViewOrderJSON,
  type ViewOrderWriteBehavior
} from '@databases/model/database';
import type * as Y from 'yjs';

const FIELD_ORDER_PREFIX = 'f:';
const ENTRY_ORDER_PREFIX = 'e:';
const ORDERING_MODE_KEY = 'm';
const GLOBAL_ORDERING_MODE = 'g';
const VIEW_SPECIFIC_ORDERING_MODE = 'v';

const normalizeOrder = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value) || !value.every((id) => typeof id === 'string')) {
    return undefined;
  }

  return [...new Set(value)];
};

export class YViewOrders {
  constructor(private readonly data: Y.Map<unknown>) {}

  get shouldPromoteToGlobal(): boolean {
    return this.hasOrders && this.data.get(ORDERING_MODE_KEY) !== GLOBAL_ORDERING_MODE;
  }

  get(viewId: string): DatabaseViewOrderJSON | undefined {
    const fieldOrder = normalizeOrder(this.data.get(`${FIELD_ORDER_PREFIX}${viewId}`));
    const entryOrder = normalizeOrder(this.data.get(`${ENTRY_ORDER_PREFIX}${viewId}`));

    return fieldOrder || entryOrder
      ? {
          ...(fieldOrder && { fieldOrder }),
          ...(entryOrder && { entryOrder })
        }
      : undefined;
  }

  setFieldOrder(
    viewId: string,
    order: readonly string[],
    behavior: ViewOrderWriteBehavior = 'activate-view-specific-ordering'
  ): void {
    this.data.set(`${FIELD_ORDER_PREFIX}${viewId}`, [...new Set(order)]);
    if (behavior === 'activate-view-specific-ordering') {
      this.markViewSpecificOrderingActive();
    }
  }

  setEntryOrder(
    viewId: string,
    order: readonly string[],
    behavior: ViewOrderWriteBehavior = 'activate-view-specific-ordering'
  ): void {
    this.data.set(`${ENTRY_ORDER_PREFIX}${viewId}`, [...new Set(order)]);
    if (behavior === 'activate-view-specific-ordering') {
      this.markViewSpecificOrderingActive();
    }
  }

  markViewSpecificOrderingActive(): void {
    if (
      this.hasOrders &&
      this.data.get(ORDERING_MODE_KEY) !== VIEW_SPECIFIC_ORDERING_MODE
    ) {
      this.data.set(ORDERING_MODE_KEY, VIEW_SPECIFIC_ORDERING_MODE);
    }
  }

  markGlobalOrderingActive(): void {
    if (this.data.get(ORDERING_MODE_KEY) !== GLOBAL_ORDERING_MODE) {
      this.data.set(ORDERING_MODE_KEY, GLOBAL_ORDERING_MODE);
    }
  }

  deleteView(viewId: string): void {
    this.data.delete(`${FIELD_ORDER_PREFIX}${viewId}`);
    this.data.delete(`${ENTRY_ORDER_PREFIX}${viewId}`);
    if (!this.hasOrders) {
      this.data.delete(ORDERING_MODE_KEY);
    }
  }

  removeField(fieldId: string): void {
    this.removeFields([fieldId]);
  }

  removeEntry(entryId: string): void {
    this.removeEntries([entryId]);
  }

  removeFields(fieldIds: readonly string[]): void {
    this.removeIds(FIELD_ORDER_PREFIX, fieldIds);
  }

  removeEntries(entryIds: readonly string[]): void {
    this.removeIds(ENTRY_ORDER_PREFIX, entryIds);
  }

  toJSON(viewIds: readonly string[]): Record<string, DatabaseViewOrderJSON> {
    return viewIds.reduce<Record<string, DatabaseViewOrderJSON>>((orders, viewId) => {
      const order = this.get(viewId);
      if (order) {
        orders[viewId] = order;
      }
      return orders;
    }, {});
  }

  merge(orders: Readonly<Record<string, DatabaseViewOrderJSON>>): void {
    Object.entries(orders).forEach(([viewId, order]) => {
      if (order.fieldOrder) {
        this.setFieldOrder(viewId, order.fieldOrder);
      }
      if (order.entryOrder) {
        this.setEntryOrder(viewId, order.entryOrder);
      }
    });
  }

  clear(): void {
    this.data.clear();
  }

  unwrap(): Y.Map<unknown> {
    return this.data;
  }

  private get hasOrders(): boolean {
    return [...this.data.keys()].some(
      (key) => key.startsWith(FIELD_ORDER_PREFIX) || key.startsWith(ENTRY_ORDER_PREFIX)
    );
  }

  private removeIds(prefix: string, ids: readonly string[]): void {
    const idsToRemove = new Set(ids);
    if (idsToRemove.size === 0) {
      return;
    }

    const updates: Array<[string, string[]]> = [];
    this.data.forEach((value, key) => {
      if (!key.startsWith(prefix)) {
        return;
      }

      const order = normalizeOrder(value);
      if (!order?.some((id) => idsToRemove.has(id))) {
        return;
      }

      updates.push([key, order.filter((id) => !idsToRemove.has(id))]);
    });
    updates.forEach(([key, order]) => this.data.set(key, order));
  }
}
